import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { MenuCatalogProductOption } from "@/modules/membership/domain/repo/list-menu-catalog-options";
import type { MembershipEligibleMenuSettings } from "@/modules/membership/domain/types";
import { Loader } from "@/shared/ui/Loader";
import { cn } from "@/shared/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  LayersIcon,
  ListIcon,
  PackageIcon,
  SearchIcon,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { membershipApi } from "../api/client";
import { membershipKeys } from "../api/queries";

const SCOPE_TYPE_OPTIONS: Array<{
  value: MembershipEligibleMenuSettings["scopeType"];
  label: string;
  hint: string;
  icon: LucideIcon;
}> = [
  {
    value: "CATEGORY",
    label: "MENU ທັງໝົດ (ຈຳກັດໄດ້)",
    hint: "ບໍ່ເລືອກສິນຄ້າ = ທຸກເມນູ MENU ທີ່ລາຄາບໍ່ເກີນກຳນົດ",
    icon: ListIcon,
  },
  {
    value: "PRODUCT",
    label: "ເລືອກເປັນສິນຄ້າ",
    hint: "ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 ສິນຄ້າ MENU",
    icon: PackageIcon,
  },
  {
    value: "VARIANT",
    label: "ເລືອກເປັນແບບ (variant)",
    hint: "ເລືອກແຕ່ລະແບບທີ່ລູກຄ້າໃຊ້ສິດໄດ້",
    icon: LayersIcon,
  },
];

function formatLak(amount: number) {
  return new Intl.NumberFormat("lo-LA").format(amount);
}

type Props = {
  value: MembershipEligibleMenuSettings;
  onChange: (next: MembershipEligibleMenuSettings) => void;
};

export function EligibleMenuScopePicker({ value, onChange }: Props) {
  const [search, setSearch] = useState("");
  const [expandedProductIds, setExpandedProductIds] = useState<Set<string>>(
    () => new Set(),
  );

  const { data, isLoading, error } = useQuery({
    queryKey: membershipKeys.eligibleMenuOptions(),
    queryFn: () => membershipApi.getEligibleMenuOptions(),
  });

  const products = data?.products ?? [];

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products
      .map((product) => {
        const productMatch = product.name.toLowerCase().includes(q);
        const variants = product.variants.filter(
          (variant) =>
            productMatch ||
            variant.name.toLowerCase().includes(q) ||
            product.name.toLowerCase().includes(q),
        );
        if (productMatch) return product;
        if (variants.length > 0) return { ...product, variants };
        return null;
      })
      .filter((p): p is MenuCatalogProductOption => p != null);
  }, [products, search]);

  const allProductIds = useMemo(
    () => products.map((p) => p.id),
    [products],
  );

  const allVariantIds = useMemo(
    () => products.flatMap((p) => p.variants.map((v) => v.id)),
    [products],
  );

  const selectedSet = useMemo(
    () => new Set(value.scopeIds),
    [value.scopeIds],
  );

  const toggleScopeId = (id: string, checked: boolean) => {
    const next = new Set(value.scopeIds);
    if (checked) next.add(id);
    else next.delete(id);
    onChange({ ...value, scopeIds: [...next] });
  };

  const setScopeType = (
    scopeType: MembershipEligibleMenuSettings["scopeType"],
  ) => {
    onChange({ ...value, scopeType, scopeIds: [] });
    setExpandedProductIds(new Set());
  };

  const selectAll = () => {
    if (value.scopeType === "VARIANT") {
      onChange({ ...value, scopeIds: [...allVariantIds] });
      return;
    }
    onChange({ ...value, scopeIds: [...allProductIds] });
  };

  const clearAll = () => {
    onChange({ ...value, scopeIds: [] });
  };

  const toggleProductExpanded = (productId: string) => {
    setExpandedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleProduct = (product: MenuCatalogProductOption, checked: boolean) => {
    toggleScopeId(product.id, checked);
  };

  const productSelectionState = (product: MenuCatalogProductOption) => {
    if (value.scopeType !== "VARIANT") {
      return selectedSet.has(product.id);
    }
    if (product.variants.length === 0) return false;
    const selectedCount = product.variants.filter((v) =>
      selectedSet.has(v.id),
    ).length;
    if (selectedCount === 0) return false;
    if (selectedCount === product.variants.length) return true;
    return "indeterminate" as const;
  };

  const toggleProductVariants = (
    product: MenuCatalogProductOption,
    checked: boolean,
  ) => {
    const next = new Set(value.scopeIds);
    for (const variant of product.variants) {
      if (checked) next.add(variant.id);
      else next.delete(variant.id);
    }
    onChange({ ...value, scopeIds: [...next] });
  };

  const selectedLabel =
    value.scopeType === "VARIANT"
      ? `${value.scopeIds.length} ແບບ`
      : value.scopeIds.length === 0 && value.scopeType === "CATEGORY"
        ? "ທັງໝົດ (ບໍ່ຈຳກັດ)"
        : `${value.scopeIds.length} ສິນຄ້າ`;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <span className="text-sm font-medium">ວິທີຈຳກັດເມນູ</span>
        <RadioGroup
          value={value.scopeType}
          onValueChange={(next) =>
            setScopeType(next as MembershipEligibleMenuSettings["scopeType"])
          }
          className="grid gap-2 lg:grid-cols-3"
        >
          {SCOPE_TYPE_OPTIONS.map((opt) => {
            const id = `eligible-scope-${opt.value}`;
            const selected = value.scopeType === opt.value;
            const Icon = opt.icon;
            return (
              <label
                key={opt.value}
                htmlFor={id}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors hover:bg-muted/50",
                  selected
                    ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                    : "border-input bg-card",
                )}
              >
                <RadioGroupItem
                  value={opt.value}
                  id={id}
                  className="mt-0.5 shrink-0"
                  hidden
                />
                <Icon
                  className={cn(
                    "size-5 shrink-0",
                    selected ? "text-primary" : "text-muted-foreground",
                  )}
                />
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="font-medium text-sm">{opt.label}</p>
                  <p className="text-muted-foreground text-xs leading-snug">
                    {opt.hint}
                  </p>
                </div>
              </label>
            );
          })}
        </RadioGroup>
      </div>

      <label className="block space-y-1.5">
        <span className="text-sm font-medium">ລາຄາສູງສຸດຕໍ່ແກ້ວ (LAK)</span>
        <Input
          type="number"
          min={0}
          value={value.maxUnitPriceLak}
          onChange={(e) =>
            onChange({
              ...value,
              maxUnitPriceLak: Number(e.target.value) || 0,
            })
          }
        />
        <p className="text-muted-foreground text-xs">
          ລູກຄ້າເລືອກໄດ້ພຽງແບບທີ່ລາຄາ ≤ ຄ່ານີ້ (ຄິດຈາກ base price)
        </p>
      </label>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium">
            ເມນູ MENU — {selectedLabel}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={selectAll}>
              ເລືອກທັງໝົດ
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={clearAll}>
              ລ້າງ
            </Button>
          </div>
        </div>

        <div className="relative">
          <SearchIcon className="text-muted-foreground pointer-events-none absolute top-2.5 left-2.5 size-4" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ຄົ້ນຫາຊື່ສິນຄ້າ ຫຼື ແບບ..."
            className="pl-9"
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : error ? (
          <p className="text-destructive text-sm">ໂຫຼດລາຍການເມນູບໍ່ສຳເລັດ</p>
        ) : filteredProducts.length === 0 ? (
          <p className="text-muted-foreground rounded-lg border border-dashed p-4 text-sm">
            ບໍ່ມີສິນຄ້າ MENU ທີ່ເປີດໃຊ້ງານ
          </p>
        ) : (
          <ScrollArea className="h-[min(420px,50vh)] rounded-lg border">
            <ul className="divide-y p-2">
              {filteredProducts.map((product) => {
                const expanded =
                  value.scopeType === "VARIANT"
                    ? expandedProductIds.has(product.id) || search.trim().length > 0
                    : false;
                const checked = productSelectionState(product);

                return (
                  <li key={product.id} className="py-1">
                    <div className="flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50">
                      {value.scopeType === "VARIANT" ? (
                        <button
                          type="button"
                          className="text-muted-foreground mt-0.5 shrink-0"
                          onClick={() => toggleProductExpanded(product.id)}
                          aria-expanded={expanded}
                        >
                          {expanded ? (
                            <ChevronDownIcon className="size-4" />
                          ) : (
                            <ChevronRightIcon className="size-4" />
                          )}
                        </button>
                      ) : (
                        <Checkbox
                          id={`eligible-product-${product.id}`}
                          checked={checked === true}
                          onCheckedChange={(val) =>
                            toggleProduct(product, val === true)
                          }
                          className="mt-0.5"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <label
                          htmlFor={
                            value.scopeType === "VARIANT"
                              ? undefined
                              : `eligible-product-${product.id}`
                          }
                          className={cn(
                            "block cursor-pointer font-medium text-sm",
                            value.scopeType === "VARIANT" && "cursor-default",
                          )}
                          onClick={
                            value.scopeType === "VARIANT"
                              ? () => toggleProductExpanded(product.id)
                              : undefined
                          }
                        >
                          {product.name}
                          <span className="text-muted-foreground ml-2 font-normal text-xs">
                            {product.variants.length} ແບບ
                          </span>
                        </label>
                        {value.scopeType === "VARIANT" && (
                          <div className="mt-1 flex items-center gap-2">
                            <Checkbox
                              id={`eligible-product-all-${product.id}`}
                              checked={
                                checked === true
                                  ? true
                                  : checked === "indeterminate"
                                    ? ("indeterminate" as const)
                                    : false
                              }
                              onCheckedChange={(val) =>
                                toggleProductVariants(
                                  product,
                                  val === true || val === "indeterminate",
                                )
                              }
                            />
                            <label
                              htmlFor={`eligible-product-all-${product.id}`}
                              className="text-muted-foreground cursor-pointer text-xs"
                            >
                              ເລືອກທຸກແບບຂອງສິນຄ້ານີ້
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    {value.scopeType === "VARIANT" && expanded && (
                      <ul className="border-muted ml-6 border-l pl-3">
                        {product.variants.map((variant) => (
                          <li
                            key={variant.id}
                            className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50"
                          >
                            <Checkbox
                              id={`eligible-variant-${variant.id}`}
                              checked={selectedSet.has(variant.id)}
                              onCheckedChange={(val) =>
                                toggleScopeId(variant.id, val === true)
                              }
                            />
                            <label
                              htmlFor={`eligible-variant-${variant.id}`}
                              className="flex flex-1 cursor-pointer items-center justify-between gap-2 text-sm"
                            >
                              <span>{variant.name}</span>
                              <span className="text-muted-foreground shrink-0 text-xs">
                                {formatLak(variant.basePriceLak)} ₭
                              </span>
                            </label>
                          </li>
                        ))}
                        {product.variants.length === 0 && (
                          <li className="text-muted-foreground px-2 py-1 text-xs">
                            ບໍ່ມີແບບ
                          </li>
                        )}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}

        {value.scopeType === "PRODUCT" && value.scopeIds.length === 0 && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-900 text-xs dark:text-amber-100">
            ກະລຸນາເລືອກຢ່າງໜ້ອຍ 1 ສິນຄ້າກ່ອນບັນທຶກ
          </p>
        )}
      </div>
    </div>
  );
}
